import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Search } from "lucide-react";
import { StudioLayout } from "./StudioLayout";
import { EmptyState, ListSkeleton } from "@/components/studio/StudioUI";
import { studio, beDate, eur, inputCls } from "@/lib/studioApi";

const METHODS = { virement: "Virement", bancontact: "Bancontact", payconiq: "Payconiq", carte: "Carte", especes: "Espèces", mollie: "En ligne", cheque: "Chèque" };

export default function StudioPayments() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  useEffect(() => { studio.get("/payments").then(({ data }) => setData(data)).catch(() => setData({ items: [], total: 0 })); }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.items || []).filter((p) => !q || [p.invoice_reference, p.client_name, p.method, p.note].some((v) => (v || "").toLowerCase().includes(q)));
  }, [data, search]);
  const total = visible.reduce((s, p) => s + p.amount, 0);
  const month = new Date().toISOString().slice(0, 7);
  const monthTotal = (data?.items || []).filter((p) => p.date.startsWith(month)).reduce((s, p) => s + p.amount, 0);

  return (
    <StudioLayout ownerOnly title="Règlements" subtitle="Tous les paiements encaissés sur vos factures, du plus récent au plus ancien.">
      <div className="mt-6 grid grid-cols-2 gap-3">
        <div data-testid="payments-month" className="rounded-2xl border border-[#E2E7F7] bg-white p-4"><p className="text-xs font-bold uppercase tracking-wide text-[#5C6A87]">Encaissé ce mois</p><p className="mt-1 font-mono text-xl font-bold text-[#0F2645]">{eur(monthTotal)}</p></div>
        <div data-testid="payments-total" className="rounded-2xl border border-[#E2E7F7] bg-white p-4"><p className="text-xs font-bold uppercase tracking-wide text-[#5C6A87]">Total encaissé</p><p className="mt-1 font-mono text-xl font-bold text-[#0F2645]">{eur(data?.total || 0)}</p></div>
      </div>
      <div className="mt-4 relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#93A0BC]" />
        <input data-testid="payments-search" className={`${inputCls} pl-9 bg-[#F6F8FD]`} placeholder="Rechercher un règlement…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <p data-testid="payments-count" className="mt-3 text-sm text-[#5C6A87]">{visible.length} règlement{visible.length > 1 ? "s" : ""} · <span className="font-mono font-bold text-[#0F2645]">{eur(total)}</span></p>
      <div data-testid="payments-list" className="mt-3 overflow-hidden rounded-2xl border border-[#E2E7F7] bg-white">
        {data === null && <ListSkeleton />}
        {visible.map((p) => (
          <Link key={p.id} to={`/studio/factures/${p.invoice_id}`} data-testid={`payment-row-${p.id}`} className="block border-b border-[#EEF1FC] px-4 py-3 last:border-b-0 hover:bg-[#F6F8FD] sm:px-5">
            <div className="flex items-center justify-between gap-3">
              <p className="font-heading text-sm font-extrabold text-[#0F2645]">{p.invoice_reference} <span className="ml-1 text-xs font-semibold text-[#93A0BC]">{beDate(p.date)}</span></p>
              <p className="font-mono text-sm font-bold text-emerald-600">+ {eur(p.amount)}</p>
            </div>
            <p className="mt-1 flex items-center gap-2 text-sm text-[#0F2645]">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600"><Coins className="h-3.5 w-3.5" /></span>
              <span className="truncate">{p.client_name}<span className="text-[#93A0BC]"> · {METHODS[p.method] || p.method}{p.note ? ` · ${p.note}` : ""}</span></span>
            </p>
          </Link>
        ))}
        {data !== null && !visible.length && <EmptyState testId="payments-empty" icon={Coins} title="Aucun règlement" hint="Les paiements enregistrés sur vos factures apparaîtront ici." />}
      </div>
    </StudioLayout>
  );
}
